import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

export interface CustomerPackaging {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: "cm" | "in";
  isDefault: boolean;
}

export interface PackagingListResponse {
  boxes: CustomerPackaging[];
  maxBoxes: number;
}

export interface CreatePackagingInput {
  name: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit?: "cm" | "in";
  isDefault?: boolean;
}

export interface UpdatePackagingInput {
  name?: string;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: "cm" | "in";
  isDefault?: boolean;
}

const PACKAGING_QUERY_KEY = ["my-packaging"];
const API_URL = import.meta.env.VITE_API_URL;

export function useMyPackaging() {
  const { token } = useSession();

  return useQuery({
    queryKey: PACKAGING_QUERY_KEY,
    queryFn: async () => {
      const { data } = await axios.get<PackagingListResponse>(
        `${API_URL}/api/v2/customers/me/boxes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export function useCreatePackaging() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePackagingInput) => {
      const { data } = await axios.post<{ box: CustomerPackaging }>(
        `${API_URL}/api/v2/customers/me/boxes`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.box;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKAGING_QUERY_KEY });
    },
  });
}

export function useUpdatePackaging() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: UpdatePackagingInput & { id: string }) => {
      const { data } = await axios.patch<{ box: CustomerPackaging }>(
        `${API_URL}/api/v2/customers/me/boxes/${id}`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.box;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKAGING_QUERY_KEY });
    },
  });
}

export function useDeletePackaging() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/customers/me/boxes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKAGING_QUERY_KEY });
    },
  });
}

export function useSetDefaultPackaging() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.patch(
        `${API_URL}/api/v2/customers/me/boxes/${id}/default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKAGING_QUERY_KEY });
    },
  });
}
